import {ArrowBack} from '@mui/icons-material';
import {Box, Chip, Container, IconButton, Paper, Typography, useTheme} from '@mui/material';
import React, {useContext, useEffect} from 'react';
import ReactMarkdown from 'react-markdown';
import {Link} from 'react-router-dom';
import ResponseGraphs from 'src/components/ResponseGraphs';
import {CoachContent} from '../object-actions/types/types';
import {ThemeContext} from '../theme/ThemeContext';
import {markAthleteReceived, markParentReceived} from '../utils/contentHelpers';
import {useRoleLabels} from "../hooks/useRoleLabels";

interface CoachContentReadOnlyViewProps {
    entity: CoachContent;
    layout?: 'full' | 'compact';
}

interface DateViewCalendarProps {
    entity: CoachContent;
}

export function DateViewCalendar({entity}: DateViewCalendarProps) {
    const {applyRoleLabels} = useRoleLabels();

    function buildLabel(time: string | null) {
        return time ? new Date(time).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        }) : 'No';
    }

    return (
        <Box sx={{display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap'}} data-testid="coach-delivery-status"
             data-attr={entity.purpose}>
            <Chip
                label={`Created: ` + new Date(entity.created_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                })}
                color={'default'}
                size="small"
            />
            <Chip
                data-testid="coach"
                label={applyRoleLabels(`Coach Delivered: `) + buildLabel(entity.coach_delivered)}
                color={entity.coach_delivered ? 'success' : 'default'}
                size="small"
            />
            <Chip
                data-testid="athlete"
                label={applyRoleLabels(`Athlete Received: `) + buildLabel(entity.athlete_received)}
                color={entity.athlete_received ? 'success' : 'default'}
                size="small"
            />
            <Chip
                data-testid="parent"
                label={applyRoleLabels(`Guardian Received: `) + buildLabel(entity.parent_received)}
                color={entity.parent_received ? 'success' : 'default'}
                size="small"
            />
        </Box>
    );
}

const CoachContentReadOnlyView: React.FC<CoachContentReadOnlyViewProps> = ({
                                                                               entity,
                                                                               layout = 'full'
                                                                           }) => {
    const theme = useTheme();
    const {brandingSettings} = useContext(ThemeContext);


    // Mark as received when athlete or parent accesses the page (only if not already marked)
    useEffect(() => {
        const markAsReceived = async () => {
            try {
                if (entity.my_roles?.includes('athlete') && !entity.athlete_received) {
                    await markAthleteReceived(Number(entity.id));
                } else if (entity.my_roles?.includes('parent') && !entity.parent_received) {
                    await markParentReceived(Number(entity.id));
                }
            } catch (error) {
                console.error('Error marking content as received:', error);
                // Don't show error to user as this is a background operation
            }
        };

        markAsReceived();
    }, [entity.id, entity.athlete_received, entity.parent_received]);

    if (!entity || entity._type !== 'CoachContent') return null;

    const showHeader = layout === 'full';
    const showResponseGraphs = layout === 'full';
    const isCompact = layout === 'compact';

    const content = (
        <Box data-testid={`coach-content-readonly--${entity.purpose}`}>
            {/* Back to progress report (when athlete context exists) */}
            {showHeader && entity.assignment?.id && (
                <Box sx={{mb: 2, display: 'flex', alignItems: 'center', gap: 1}}>
                    <IconButton
                        component={Link}
                        to={`/program-progress/${entity.assignment.id}`}
                        color="secondary"
                        aria-label="Back to progress report"
                    >
                        <ArrowBack/>
                    </IconButton>
                    <Typography component={Link} to={`/program-progress/${entity.assignment.id}`} variant="body2"
                                color="text.secondary"
                                sx={{textDecoration: 'none', '&:hover': {textDecoration: 'underline'}}}>
                        Back to progress report
                    </Typography>
                </Box>
            )}
            {/* Header */}
            {showHeader && (
                <Box sx={{mb: 4, display: 'flex', alignItems: 'flex-start', gap: 3}}>
                    <Box sx={{flex: 1, minWidth: 0}}>
                        <Typography
                            variant="h3"
                            component="h1"
                            sx={{
                                fontWeight: 'bold',
                                color: 'secondary.main',
                                mb: 1
                            }}
                        >
                            {entity.title}
                        </Typography>
                        <DateViewCalendar entity={entity} />
                    </Box>

                </Box>
            )}

            {/* ResponseGraphs Section */}
            {showResponseGraphs && (
                <ResponseGraphs
                    entity={entity.athlete}
                    title="Athlete Performance Overview"
                    height={250}
                />
            )}

            {/* Content Body */}
            <Box sx={{mb: 4}} data-testid="coach-content-readonly-body">
                {
                    <Box
                        sx={{
                            '& p': {margin: 0},
                            '& p + p': {marginTop: 2},
                            '& h1, & h2, & h3, & h4, & h5, & h6': {
                                marginTop: 3,
                                marginBottom: 2,
                                fontWeight: 'bold',
                                color: 'text.primary',
                                fontFamily: brandingSettings?.typography?.fontFamily || theme.typography.fontFamily
                            },
                            '& ul, & ol': {
                                marginTop: 2,
                                marginBottom: 2,
                                paddingLeft: 3
                            },
                            '& li': {
                                marginBottom: 1
                            },
                            '& a': {
                                color: theme.palette.primary.main,
                                textDecoration: 'none',
                                '&:hover': {
                                    textDecoration: 'underline'
                                }
                            },
                            '& blockquote': {
                                borderLeft: `4px solid ${theme.palette.primary.main}`,
                                paddingLeft: 2,
                                marginLeft: 0,
                                fontStyle: 'italic',
                                color: 'text.secondary',
                                backgroundColor: brandingSettings?.palette?.[theme.palette.mode]?.paper?.main || (theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50'),
                                padding: 2,
                                borderRadius: 1
                            },
                            '& code': {
                                backgroundColor: brandingSettings?.palette?.[theme.palette.mode]?.paper?.main || (theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100'),
                                padding: '2px 4px',
                                borderRadius: 1,
                                fontFamily: 'monospace'
                            },
                            '& pre': {
                                backgroundColor: brandingSettings?.palette?.[theme.palette.mode]?.paper?.main || (theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100'),
                                padding: 2,
                                borderRadius: 1,
                                overflow: 'auto'
                            }
                        }}
                    >
                        <ReactMarkdown>{entity.body}</ReactMarkdown>
                    </Box>}
            </Box>
        </Box>
    );

    if (isCompact) {
        return content;
    }

    return (
        <Container maxWidth="lg" sx={{py: 4}}>
            <Paper elevation={2} sx={{p: 4, borderRadius: 2}}>
                {content}
            </Paper>
        </Container>
    );
};

export default CoachContentReadOnlyView;
